import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Download, Eye, LucideAngularModule, Plus, Search, Trash } from 'lucide-angular';
import { EventService } from '../shared/event.service';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { EventModel, EventModelUtils } from '../shared/event.model';
import { RouterLink } from '@angular/router';
import { FormControl } from '@angular/forms';
import { map, switchMap, takeUntil, takeWhile, timer } from 'rxjs';
import { EventExportModel } from '../shared/event-export.model';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { DataTableComponent } from '@app/partials/datatable/data-table/data-table.component';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { RouteParams } from '@app/shared/abstract/route-params';
import { Column } from '@app/partials/datatable/data-table/column';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { DatePickerComponent } from '@app/partials/form/date-picker/date-picker.component';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { AuthService } from '@app/auth/auth.service';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { ToastService } from '@app/shared/toast/toast.service';

@Component({
  selector: 'app-index11',
  imports: [DataTableComponent, LucideAngularModule, RouterLink, TextInputComponent, DatePickerComponent, ButtonComponent, ModalComponent],
  providers: [provideFlatpickrDefaults(), DatePipe],
  templateUrl: './events-index.component.html',
  styleUrl: './events-index.component.css',
})
export class EventsIndexComponent extends Destroyer implements OnInit {
  constructor(
    private service: EventService,
    private authService: AuthService,
    private toastService: ToastService,
    private featureToggleService: FeatureToggleService,
    private datePipe: DatePipe
  ) {
    super();
    this.initRowActions();
  }

  protected readonly PlusIcon = Plus;
  protected readonly EyeIcon = Eye;
  protected readonly SearchIcon = Search;
  protected readonly TrashIcon = Trash;
  protected readonly DownloadIcon = Download;

  // TVG-152: CSV export runs as a background job, gated behind the
  // "event-export" feature toggle. `exporting` covers the queued/building phase;
  // once ready a download link is shown on screen.
  protected exportEnabled = false;
  protected exporting = false;
  protected exportReady = false;
  private readyExportId: number | null = null;
  private readyFileName: string | null = null;

  // Poll the export every 3s while it builds.
  private readonly EXPORT_POLL_MS = 3000;

  protected eventsPage?: PagedEntities<EventModel>;
  protected loading = false;
  protected canCreateEvents = false;
  protected isAdmin = false;

  protected isDeleteModalOpen = false;
  protected isDeleting = false;
  protected eventToDelete: EventModel | null = null;

  routeParams = new RouteParams({
    pageNum: 1,
    pageSize: 50,
  });

  tableColumns: Column<EventModel>[] = [
    { field: 'reference', header: 'Ref', width: '10%' },
    { field: 'name', header: 'Name', sortable: true, width: '30%' },
    { field: 'start_date', type: 'date', header: 'Event Date', sortable: true, width: '10%' },
    {
      field: 'planner',
      header: 'Planner',
      width: '15%',
      callback: (item: EventModel) => {
        const parts: string[] = [];

        if (item.sales_manager_name) {
          parts.push(item.sales_manager_name);
        }
        if (item.event_manager_name) {
          parts.push(item.event_manager_name);
        }

        if (parts.length > 0) {
          return parts.join(', ');
        }

        // Fallback to creator if no managers assigned
        return item.created_by_name || '';
      },
    },
    {
      field: 'category',
      header: 'Category',
      type: 'badge',
      sortable: true,
      width: '10%',
      getColor: (item: EventModel) => EventModelUtils.getCategoryColor(item.category),
    },
    {
      field: 'status',
      header: 'Status',
      type: 'badge',
      sortable: true,
      width: '10%',
      getColor: (item: EventModel) => EventModelUtils.getStatusColor(item.status),
    },
  ];

  rowActions: any[] = [];

  private initRowActions() {
    this.rowActions = [
      {
        icon: this.EyeIcon,
        routerLink: (item: EventModel) => ['/events', item.id],
      },
      {
        icon: this.TrashIcon,
        callback: (item: EventModel) => this.deleteEvent(item),
        condition: () => this.isAdmin,
        classes: 'text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500',
        title: () => 'Delete Event',
      },
    ];
  }

  searchControl = new FormControl();
  startDateControl = new FormControl();
  endDateControl = new FormControl();

  loadData() {
    this.loading = true;
    this.service.getPage(this.routeParams).subscribe((res) => {
      this.eventsPage = res;
      this.loading = false;
    });
  }

  ngOnInit(): void {
    this.exportEnabled = this.featureToggleService.isEnabled('event-export');
    this.authService.getUser().pipe(takeUntil(this.destroy$)).subscribe({
      next: (user) => {
        this.isAdmin = user?.roles?.includes('admin') ?? false;
        // TVG-352: raising an event is for admins and managers.
        this.canCreateEvents = user?.roles?.some((role) => ['admin', 'manager'].includes(role)) ?? false;
        this.initRowActions();
      },
      error: () => {
        this.isAdmin = false;
        this.canCreateEvents = false;
        this.initRowActions();
      },
    });
    this.loadData();
  }

  performSearch(): void {
    this.updateFilters();
  }

  // TVG-152: Queue a background export, then poll until the CSV is ready. The
  // request never blocks on the build, so it cannot time out on large volumes.
  exportCsv(): void {
    if (this.exporting) {
      return;
    }

    this.exporting = true;
    this.exportReady = false;
    this.readyExportId = null;
    this.readyFileName = null;

    this.service.requestExport().pipe(
      switchMap((created) => {
        const exportId = created.data.id as number;

        return timer(0, this.EXPORT_POLL_MS).pipe(
          switchMap(() => this.service.getExport(exportId)),
          map((polled) => polled.data),
          // Keep polling while pending/processing; emit the terminal state too.
          takeWhile((model) => model.status === 'pending' || model.status === 'processing', true),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (model: EventExportModel) => {
        if (model.status === 'completed') {
          this.exporting = false;
          this.exportReady = true;
          this.readyExportId = model.id ?? null;
          this.readyFileName = model.file_name ?? null;
          this.toastService.success('Export ready to download');
          this.downloadReadyExport();
        } else if (model.status === 'failed') {
          this.exporting = false;
          this.toastService.error('Export failed. Please try again.');
        }
      },
      error: () => {
        this.exporting = false;
        this.toastService.error('Error exporting events');
      },
    });
  }

  downloadReadyExport(): void {
    if (!this.readyExportId) {
      return;
    }

    const fallbackName = `events-export-${this.datePipe.transform(new Date(), 'yyyy-MM-dd')}.csv`;
    this.service.downloadExport(this.readyExportId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.readyFileName || fallbackName;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastService.error('Failed to download export');
      },
    });
  }

  private updateFilters(): void {
    const filters: { field: string; value: string }[] = [];

    // Add search filter if present
    if (this.searchControl.value) {
      filters.push({ field: 'search', value: this.searchControl.value });
    }

    // VG-397: Add date range filter if both dates are present
    if (this.startDateControl.value && this.endDateControl.value) {
      filters.push({
        field: 'date_range',
        value: `${this.startDateControl.value},${this.endDateControl.value}`
      });
    }

    this.routeParams.filter = filters;
    this.loadData();
  }

  handleRoute(params: RouteParams) {
    this.routeParams.sort = params.sort;
    this.routeParams.pageNum = params.pageNum;
    this.routeParams.pageSize = params.pageSize;
    this.loadData();
  }

  deleteEvent(event: EventModel): void {
    this.eventToDelete = event;
    this.isDeleteModalOpen = true;
  }

  confirmDelete(): void {
    if (!this.eventToDelete?.id || this.isDeleting) return;

    this.isDeleting = true;

    this.service.delete(this.eventToDelete.id.toString()).subscribe({
      next: () => {
        this.isDeleteModalOpen = false;
        this.isDeleting = false;
        this.eventToDelete = null;
        this.toastService.success('Event deleted successfully');
        this.loadData();
      },
      error: (error) => {
        this.isDeleting = false;
        this.toastService.error(error.error?.message || 'Failed to delete event');
      }
    });
  }
}
